/**
 * Single source of truth for FIVE LENS Enneagram product pricing and catalogue.
 *
 * Product                                | Southern Africa (ZAR, excl VAT) | UK & Ireland (GBP) | Rest of world (USD)
 * ---------------------------------------|---------------------------------|--------------------|--------------------
 * Practitioner Certification             | R12 000                         | £900               | $1,200
 * Team Facilitator Certification         | R18 000                         | £1,350             | $1,800
 * Practitioner + Team Facilitator Bundle | R24 000                         | £1,800             | $2,400
 * Bundle saving                          | R6 000                          | £450               | $600
 *
 * Rules:
 * - Derived from ZAR figure: USD = ZAR ÷ 10; GBP = USD ÷ 1.33, rounded to nearest £10.
 * - South African VAT is 15%: R12 000 → R13 800, R18 000 → R20 700, R24 000 → R27 600.
 * - Formatting:
 *   - ZAR: "R12 000" (space as thousands separator)
 *   - GBP: "£900" / "£1,350" (comma as thousands separator)
 *   - USD: "$1,200" / "$1,800" (comma as thousands separator, no "USD" suffix)
 */

export const VAT_RATE = 0.15;

export type SupportedRegion = 'za' | 'uk' | 'row';
export type SupportedCurrency = 'ZAR' | 'GBP' | 'USD';

export const ZA_REGION_COUNTRIES = ['ZA', 'NA', 'BW', 'LS', 'SZ', 'ZW', 'MZ', 'ZM', 'MW'];
export const UK_REGION_COUNTRIES = ['GB', 'IE'];

export function formatZar(amount: number): string {
  const str = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `R${str}`;
}

export function formatGbp(amount: number): string {
  const str = Math.round(amount).toLocaleString('en-GB');
  return `£${str}`;
}

export function formatUsd(amount: number): string {
  const str = Math.round(amount).toLocaleString('en-US');
  return `$${str}`;
}

export interface RegionalPrice {
  amount: number;
  formatted: string;
  formattedAmount: string;
}

export interface ZarRegionalPrice extends RegionalPrice {
  amountExclVat: number;
  vatAmount: number;
  amountInclVat: number;
  formattedExclVat: string;
  formattedInclVat: string;
  formattedVat: string;
}

export interface ProductPricing {
  id: string;
  name: string;
  zar: ZarRegionalPrice;
  gbp: RegionalPrice;
  usd: RegionalPrice;
  note?: string;
  featured?: boolean;
  features?: string[];
  cta?: {
    label: string;
    href: string;
  };
}

export function derivePricesFromZar(zarExclVat: number) {
  const vatAmount = Math.round(zarExclVat * VAT_RATE);
  const amountInclVat = zarExclVat + vatAmount;

  const usdAmount = Math.round(zarExclVat / 10);
  const gbpAmount = Math.round((usdAmount / 1.33) / 10) * 10;

  return {
    zar: {
      amount: zarExclVat,
      formatted: formatZar(zarExclVat),
      formattedAmount: formatZar(zarExclVat),
      amountExclVat: zarExclVat,
      vatAmount,
      amountInclVat,
      formattedExclVat: formatZar(zarExclVat),
      formattedInclVat: formatZar(amountInclVat),
      formattedVat: formatZar(vatAmount),
    },
    gbp: {
      amount: gbpAmount,
      formatted: formatGbp(gbpAmount),
      formattedAmount: formatGbp(gbpAmount),
    },
    usd: {
      amount: usdAmount,
      formatted: formatUsd(usdAmount),
      formattedAmount: formatUsd(usdAmount),
    },
  };
}

export const bundleSaving = {
  id: 'bundle-saving',
  name: 'Bundle Saving',
  ...derivePricesFromZar(6000),
};

export const pricingTable: Record<string, ProductPricing> = {
  'practitioner-certification': {
    id: 'practitioner-certification',
    name: 'Practitioner Certification',
    ...derivePricesFromZar(12000),
    features: [
      '8-10 hours self-paced pre-course',
      'Six 3-hour live Zoom sessions',
      'Full diagnostic toolkit access',
    ],
    cta: {
      label: 'Register Now',
      href: '/register',
    },
  },
  'certification-bundle': {
    id: 'certification-bundle',
    name: 'Practitioner + Team Facilitator Bundle',
    ...derivePricesFromZar(24000),
    features: [
      'Level 1 Practitioner Certification',
      'Level 2 Team Facilitator Certification',
      'Advanced team dynamics mapping',
    ],
    cta: {
      label: 'Secure Bundle',
      href: '/register',
    },
    featured: true,
  },
  'team-facilitator-certification': {
    id: 'team-facilitator-certification',
    name: 'Team Facilitator Certification',
    ...derivePricesFromZar(18000),
    note: 'Requires FIVE LENS Practitioner Certification (Level 1)',
    features: [
      'Team effectiveness methodologies',
      'Resolving systemic conflict',
      'Enterprise deployment readiness',
    ],
    cta: {
      label: 'Register Now',
      href: '/register',
    },
  },
};

export function getProductPricing(id: string): ProductPricing | undefined {
  return pricingTable[id];
}

export function resolveRegion(countryCode?: string | null, queryRegion?: string | null): {
  region: SupportedRegion;
  currency: SupportedCurrency;
  vat: boolean;
  country: string;
} {
  if (queryRegion) {
    const q = queryRegion.toLowerCase().trim();
    if (q === 'za') return { region: 'za', currency: 'ZAR', vat: true, country: 'ZA' };
    if (q === 'uk') return { region: 'uk', currency: 'GBP', vat: false, country: 'GB' };
    if (q === 'row') return { region: 'row', currency: 'USD', vat: false, country: 'US' };
  }

  const clean = (countryCode || '').toUpperCase().trim();
  if (ZA_REGION_COUNTRIES.includes(clean)) {
    return {
      region: 'za',
      currency: 'ZAR',
      vat: clean === 'ZA',
      country: clean,
    };
  }

  if (UK_REGION_COUNTRIES.includes(clean)) {
    return {
      region: 'uk',
      currency: 'GBP',
      vat: false,
      country: clean,
    };
  }

  return {
    region: 'row',
    currency: 'USD',
    vat: false,
    country: clean || 'US',
  };
}
